import { usePortalThemeConfigurationQuery } from 'src/api/api.generated'
import useErrorSnackBar from '../hooks/error-snack-bar/use-error-snack-bar'
import { createTheme } from '@mui/material'
import { DEFAULT_BACKGROUND_SEARCH_COLOR, DEFAULT_FONT_FAMILY, DEFAULT_PRIMARY_COLOR, DEFAULT_WELCOME_TEXT_COLOR, defaultInputStyle, GREY_100_TAILWIND } from './theme'
import { useEffect } from 'react'

export default function useGetTheme({ propertyId, tenantId }: { propertyId: string | null; tenantId: string | null }) {
  const showError = useErrorSnackBar()

  if (!process.env.REACT_APP_PUBLIC_TOKEN) {
    throw new Error('REACT_APP_PUBLIC_TOKEN is not defined')
  }

  const skipQuery = !propertyId || !tenantId

  const { data, loading, called } = usePortalThemeConfigurationQuery({
    variables: {
      filter: {
        tenantId: { eq: tenantId },
        id: { eq: propertyId },
      },
    },
    skip: skipQuery,
    onError: showError,
    context: {
      headers: {
        Authorization: `Bearer ${process.env.REACT_APP_PUBLIC_TOKEN}`,
      },
    },
  })

  const personalizedTheme = data?.properties?.[0]?.bookingEngineConfiguration ?? undefined

  const fontFamily = personalizedTheme?.font ?? DEFAULT_FONT_FAMILY

  useEffect(() => {
    if (!loading && called && !skipQuery) {
      document.querySelector('head')?.insertAdjacentHTML(
        'beforeend',
        `<style>
@import url('https://fonts.googleapis.com/css2?family=${fontFamily.replaceAll(' ', '+')}:wght@400;700&display=swap');
        </style>`,
      )
    }
  }, [fontFamily, loading, skipQuery, called])

  const theme = createTheme({
    typography: {
      fontFamily: fontFamily,
    },
    palette: {
      welcomeTextColor: {
        main: personalizedTheme?.welcomeTextColor ?? DEFAULT_WELCOME_TEXT_COLOR,
      },
      background: {
        default: GREY_100_TAILWIND,
        paper: GREY_100_TAILWIND,
      },
      primary: {
        main: personalizedTheme?.primaryColor ?? DEFAULT_PRIMARY_COLOR,
      },
      secondary: {
        main: personalizedTheme?.secondaryColor ?? DEFAULT_BACKGROUND_SEARCH_COLOR,
      },
      text: {
        primary: '#222831',
      },
    },
    components: {
      MuiButton: {
        variants: [
          {
            props: { variant: 'text' },
            style: {
              padding: 0,
            },
          },
        ],
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontSize: '1rem',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: '#222831',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: defaultInputStyle,
        },
      },
      MuiSelect: {
        styleOverrides: {
          outlined: defaultInputStyle,
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            margin: 0,
          },
        },
      },
    },
  })

  return { theme, loading }
}
